import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { apiRequest, ApiRequestError } from '../../api/client.js';
import type { EdpEmployeeView, EdpFile, EdpItem } from '../../api/employeeDocsTypes.js';
import { Alert, Button, Card, Field, Spinner, TextInput } from '../../components/ui.js';
import { LinkAccessShell } from '../../employeeDocs/LinkAccessShell.js';
import {
  FileList,
  FilePicker,
  FormDirections,
  FormFieldGrid,
  ItemStatusBadge,
  PackageStatusBadge,
  RejectionNote,
  type FormValues,
  type PickedFile,
} from '../../employeeDocs/shared.js';
import { formatDate, formatDateTime } from '../../lib/format.js';
import { validateFormAnswers } from '../../../shared/formValidation.js';

export function MyDocumentsPage() {
  const { token = '' } = useParams();
  return (
    <LinkAccessShell prefix="my-documents" token={token} heading="Your documents">
      {(_preview, signOut) => <EmployeeChecklist token={token} signOut={signOut} />}
    </LinkAccessShell>
  );
}

function EmployeeChecklist({ token, signOut }: { token: string; signOut: () => Promise<void> }) {
  const queryClient = useQueryClient();
  const queryKey = ['edp', 'my-documents', token, 'package'];
  const view = useQuery({
    queryKey,
    queryFn: () => apiRequest<EdpEmployeeView>(`/api/my-documents/${token}/package`),
    retry: false,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey });

  const submit = useMutation({
    mutationFn: () => apiRequest(`/api/my-documents/${token}/submit`, { method: 'POST' }),
    onSuccess: () => void refresh(),
  });

  if (view.isLoading) return <Spinner label="Loading your checklist" />;
  if (view.isError || !view.data) {
    return (
      <Alert variant="error" title="Your session has ended">
        <Button variant="secondary" className="mt-3" onClick={() => void signOut()}>
          Start again
        </Button>
      </Alert>
    );
  }

  const { package: pkg, items, completeness } = view.data;
  const editable =
    pkg.status === 'invited' ||
    pkg.status === 'in_progress' ||
    pkg.status === 'corrections_requested';
  const outstandingById = new Map(
    completeness.outstanding.map((entry) => [entry.itemId, entry.reason]),
  );

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{pkg.title}</h2>
            <p className="text-sm text-slate-600">
              {pkg.employeeName}
              {pkg.dueAt && ` · due ${formatDate(pkg.dueAt)}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <PackageStatusBadge status={pkg.status} />
            <Button variant="ghost" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        </div>

        {pkg.status === 'submitted' && (
          <Alert variant="success" className="mt-4" title="Submitted">
            Your package was submitted on {formatDateTime(pkg.submittedAt)} and is being reviewed by
            VGS. You will be emailed if anything needs correcting.
          </Alert>
        )}
        {pkg.status === 'corrections_requested' && (
          <Alert variant="warning" className="mt-4" title="Corrections requested">
            VGS reviewed your package and returned the items marked below. Fix those items and
            submit again; everything else is already accepted.
          </Alert>
        )}
        {pkg.status === 'released' && (
          <Alert variant="success" className="mt-4" title="Complete">
            Your package has been approved and sent to USPS. Nothing further is needed from you.
          </Alert>
        )}
        {pkg.status === 'cancelled' && (
          <Alert variant="info" className="mt-4" title="Cancelled">
            This request was cancelled by VGS.
          </Alert>
        )}
      </Card>

      {editable && (
        <Card title="What is still needed">
          {completeness.complete ? (
            <Alert variant="success">
              Everything is complete. Review your entries below, then submit.
            </Alert>
          ) : (
            <ul className="list-disc space-y-1 pl-5 text-sm text-slate-800">
              {completeness.outstanding.map((entry) => (
                <li key={entry.itemId}>
                  <span className="font-medium">{entry.name}</span> — {entry.reason}
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {items.map((item) => (
        <ItemCard
          key={item.id}
          token={token}
          item={item}
          editable={editable && item.status !== 'accepted'}
          outstanding={outstandingById.get(item.id) ?? null}
          onChanged={refresh}
        />
      ))}

      {editable && (
        <Card>
          {submit.isError && (
            <Alert variant="error" className="mb-3">
              {submit.error instanceof ApiRequestError
                ? submit.error.message
                : 'The package could not be submitted.'}
            </Alert>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-600">
              {completeness.complete
                ? 'Submitting sends your package to the VGS reviewer. You cannot change it after that unless it is returned.'
                : `${completeness.outstanding.length} item(s) must be finished before you can submit.`}
            </p>
            <Button
              disabled={!completeness.complete}
              loading={submit.isPending}
              onClick={() => submit.mutate()}
            >
              Submit for review
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function ItemCard({
  token,
  item,
  editable,
  outstanding,
  onChanged,
}: {
  token: string;
  item: EdpItem;
  editable: boolean;
  outstanding: string | null;
  onChanged: () => Promise<void>;
}) {
  return (
    <Card
      title={
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-semibold text-slate-900">{item.name}</h2>
          <ItemStatusBadge status={item.status} />
          {!item.isRequired && <span className="text-xs text-slate-500">optional</span>}
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {item.instructions && <p className="text-sm text-slate-700">{item.instructions}</p>}
        <RejectionNote item={item} />
        {outstanding && item.status !== 'rejected' && editable && (
          <p className="text-sm text-amber-800">{outstanding}</p>
        )}
        {item.kind === 'form' ? (
          <FormItem token={token} item={item} editable={editable} onChanged={onChanged} />
        ) : (
          <CredentialItem token={token} item={item} editable={editable} onChanged={onChanged} />
        )}
      </div>
    </Card>
  );
}

function FormItem({
  token,
  item,
  editable,
  onChanged,
}: {
  token: string;
  item: EdpItem;
  editable: boolean;
  onChanged: () => Promise<void>;
}) {
  const [values, setValues] = useState<FormValues>(item.formValues ?? {});
  const [signedName, setSignedName] = useState('');
  const [dirty, setDirty] = useState(false);
  // Checks are shown once the employee has saved something (not on a blank form).
  const [checking, setChecking] = useState(
    Object.keys(item.formValues ?? {}).length > 0 || item.status === 'rejected',
  );

  const save = useMutation({
    mutationFn: (input: { values: FormValues; signedName?: string }) =>
      apiRequest<{ missing: string[] }>(`/api/my-documents/${token}/items/${item.id}/form`, {
        method: 'PUT',
        body: input,
      }),
    onSuccess: async () => {
      setDirty(false);
      setChecking(true);
      await onChanged();
    },
  });

  const upload = useMutation({
    mutationFn: (file: PickedFile) =>
      apiRequest(`/api/my-documents/${token}/items/${item.id}/files`, {
        method: 'POST',
        body: file,
      }),
    onSuccess: () => void onChanged(),
  });
  const remove = useMutation({
    mutationFn: (file: EdpFile) =>
      apiRequest(`/api/my-documents/${token}/items/${item.id}/files/${file.id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => void onChanged(),
  });

  const fields = item.formFields ?? [];
  const ink = item.requiresSignature && item.signatureMode === 'ink';
  const rendered = item.files.find((file) => file.isRenderedForm);
  const signedScan = item.files.find((file) => file.isSignedScan);
  const missing = save.data?.missing;
  // Same checks the server applies, run as the employee types so the message sits next to the box.
  const issues = checking ? validateFormAnswers(fields, item.formRules ?? [], values) : [];

  return (
    <div className="flex flex-col gap-4">
      {fields.length === 0 && (
        <p className="text-sm text-slate-500">This form has no fields to fill in.</p>
      )}
      {editable && issues.length > 0 && (
        <Alert
          variant="warning"
          title={`${issues.length} item${issues.length === 1 ? '' : 's'} still need attention`}
        >
          Each is marked in red below. You can save your progress at any time, but this form cannot
          be submitted until every item is corrected.
        </Alert>
      )}
      <FormFieldGrid
        fields={fields}
        values={values}
        disabled={!editable}
        issues={issues}
        onChange={(name, value) => {
          setDirty(true);
          setValues((current) => ({ ...current, [name]: value }));
        }}
      />

      {ink && (
        <div className="flex flex-col gap-3 rounded-md bg-slate-50 p-3 ring-1 ring-slate-200">
          {item.signingStatement && <FormDirections text={item.signingStatement} />}
          <p className="text-sm font-medium text-slate-800">This form must be signed by hand.</p>
          <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-700">
            <li>Fill in every field above and click Save progress.</li>
            <li>
              {rendered ? (
                <a
                  href={`/api/my-documents/${token}/files/${rendered.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-brand-700 hover:underline"
                >
                  Download the completed form (PDF)
                </a>
              ) : (
                'Download the completed form (available once every field is filled in)'
              )}
              , print it and sign it in ink.
            </li>
            <li>
              Scan the signed form to PDF and upload it below. Only PDF files are accepted —
              photographs and image files (JPG, PNG) will be refused.
            </li>
          </ol>
          {signedScan ? (
            <FileList
              files={[signedScan]}
              hrefFor={(file) => `/api/my-documents/${token}/files/${file.id}`}
              onRemove={editable ? (file) => remove.mutate(file) : undefined}
              removing={remove.isPending}
            />
          ) : (
            <p className="text-sm text-slate-600">No signed copy uploaded yet.</p>
          )}
          {remove.isError && (
            <Alert variant="error">
              {remove.error instanceof ApiRequestError
                ? remove.error.message
                : 'The file could not be removed.'}
            </Alert>
          )}
          {editable && rendered && !dirty && (
            <FilePicker
              label={signedScan ? 'Replace the signed copy' : 'Upload the signed copy'}
              onPick={(file) => upload.mutateAsync(file).then(() => undefined)}
            />
          )}
          {editable && dirty && (
            <p className="text-xs text-slate-500">
              Save your answers first; changing an answer replaces the printout you need to sign.
            </p>
          )}
        </div>
      )}

      {item.requiresSignature && !ink && (
        <div className="rounded-md bg-slate-50 p-3 ring-1 ring-slate-200">
          {item.signingStatement && (
            <FormDirections text={item.signingStatement} className="mb-3" />
          )}
          {item.signedAt ? (
            <p className="text-sm text-slate-800">
              Signed by <span className="font-medium">{item.signedName}</span> on{' '}
              {formatDateTime(item.signedAt)}.
              {editable && ' Changing any answer will require signing again.'}
            </p>
          ) : (
            <Field
              label="Sign by typing your full legal name"
              htmlFor={`sign-${item.id}`}
              required
              hint="Typing your name and saving is your electronic signature. The date, time and network address are recorded."
            >
              <TextInput
                id={`sign-${item.id}`}
                value={signedName}
                disabled={!editable}
                onChange={(event) => setSignedName(event.target.value)}
              />
            </Field>
          )}
        </div>
      )}

      {save.isError && (
        <Alert variant="error">
          {save.error instanceof ApiRequestError
            ? save.error.message
            : 'Your answers could not be saved.'}
        </Alert>
      )}
      {missing && missing.length > 0 && !dirty && (
        <Alert variant="warning">
          Saved, but not yet complete. Still needed: {missing.join('; ')}.
        </Alert>
      )}
      {missing && missing.length === 0 && !dirty && (
        <Alert variant="success">Saved and complete.</Alert>
      )}

      {rendered && !ink && (
        <p className="text-sm">
          <a
            href={`/api/my-documents/${token}/files/${rendered.id}`}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-brand-700 hover:underline"
          >
            View the completed form (PDF)
          </a>
        </p>
      )}

      {editable && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            loading={save.isPending}
            onClick={() => save.mutate({ values })}
          >
            Save progress
          </Button>
          {item.requiresSignature && !ink && (!item.signedAt || dirty) && (
            <Button
              loading={save.isPending}
              disabled={signedName.trim().length < 2}
              onClick={() => save.mutate({ values, signedName: signedName.trim() })}
            >
              Sign and save
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function CredentialItem({
  token,
  item,
  editable,
  onChanged,
}: {
  token: string;
  item: EdpItem;
  editable: boolean;
  onChanged: () => Promise<void>;
}) {
  const upload = useMutation({
    mutationFn: (file: PickedFile) =>
      apiRequest(`/api/my-documents/${token}/items/${item.id}/files`, {
        method: 'POST',
        body: file,
      }),
    onSuccess: () => void onChanged(),
  });
  const remove = useMutation({
    mutationFn: (file: EdpFile) =>
      apiRequest(`/api/my-documents/${token}/items/${item.id}/files/${file.id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => void onChanged(),
  });

  const need =
    item.minFiles === item.maxFiles ? `${item.minFiles}` : `${item.minFiles}–${item.maxFiles}`;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-slate-600">
        Upload {need} file(s) as PDF. Photographs and image files (JPG, PNG) are not accepted.{' '}
        {item.minFiles > 1 && 'For a card, upload the front and the back.'}
      </p>
      <FileList
        files={item.files}
        hrefFor={(file) => `/api/my-documents/${token}/files/${file.id}`}
        onRemove={editable ? (file) => remove.mutate(file) : undefined}
        removing={remove.isPending}
      />
      {remove.isError && (
        <Alert variant="error">
          {remove.error instanceof ApiRequestError
            ? remove.error.message
            : 'The file could not be removed.'}
        </Alert>
      )}
      {editable && item.files.length < item.maxFiles && (
        <FilePicker
          label="Choose a file"
          onPick={(file) => upload.mutateAsync(file).then(() => undefined)}
        />
      )}
      {editable && item.status === 'rejected' && item.files.length >= item.maxFiles && (
        <p className="text-sm text-slate-600">
          Remove the returned file above, then upload a replacement.
        </p>
      )}
    </div>
  );
}
